import React, { useState } from 'react';
import {
  Box,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  Button,
  TextArea,
  AlertCircleIcon,
  useToast,
} from '@razorpay/blade/components';
import { useMutation, useQueryClient } from 'react-query';
import { FETCH_BANK_ACCOUNTS, SEND_FOR_APPROVAL } from 'api/employeeBankAccount';
import api from 'api';
import { AppError } from 'utils/AppError';
import { parseError } from 'utils/Api';

export const NameMismatchModal = ({
  isOpen,
  onDismiss,
  accountDetails,
}: {
  isOpen: boolean;
  onDismiss: () => void;
  accountDetails: {
    beneficiaryName: string;
    employeeName: string;
    bankAccountId: number | string;
  };
}) => {
  const [comment, setComment] = useState('');
  const { beneficiaryName, employeeName, bankAccountId } = accountDetails || {};

  const toast = useToast();
  const queryClient = useQueryClient();

  const mutateSendForApproval = useMutation({
    mutationKey: SEND_FOR_APPROVAL,
    mutationFn: api.employeeBankAccount.sendForApproval,
    onSuccess: () => {
      onDismiss();
      toast.show({
        content: 'Bank Account Added Successfully',
        color: 'positive',
      });
      queryClient.invalidateQueries([FETCH_BANK_ACCOUNTS]);
    },
    onError: (e: typeof AppError) => {
      toast.show({
        content: parseError(e),
        color: 'negative',
      });
      onDismiss();
    },
  });
  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} size="small">
      <ModalHeader title="Update Bank Details" />
      <ModalBody>
        <Box
          borderColor="surface.border.gray.muted"
          borderWidth="thin"
          borderRadius="large"
          marginBottom="spacing.6"
          overflow="hidden">
          <Box
            padding="spacing.5"
            backgroundColor="surface.background.gray.moderate"
            display="flex"
            alignItems="center"
            gap="spacing.3">
            <Box
              padding="spacing.2"
              backgroundColor="surface.background.gray.intense"
              borderRadius="small">
              <AlertCircleIcon color="feedback.icon.notice.intense" size="small" />
            </Box>
            <Text weight="semibold">Name Mismatch</Text>
          </Box>
          <Box display="flex" gap="spacing.5" padding="spacing.5">
            <Box width="50%" display="flex" flexDirection="column" gap="spacing.2">
              <Text color="surface.text.gray.muted" weight="regular">
                Employee Name
              </Text>
              <Text color="surface.text.gray.subtle" weight="regular">
                {employeeName}
              </Text>
            </Box>

            <Box display="flex" flexDirection="column" gap="spacing.2">
              <Text color="surface.text.gray.muted" weight="regular">
                Account Holder Name
              </Text>
              <Text color="surface.text.gray.subtle" weight="regular">
                {beneficiaryName}
              </Text>
            </Box>
          </Box>
        </Box>
        <Box marginTop="spacing.6">
          <TextArea
            value={comment}
            onChange={({ value }) => {
              setComment(value ?? '');
            }}
            numberOfLines={2}
            label="Comment "
            isRequired={false}
            necessityIndicator="optional"
            placeholder="Why are you approving this?"
            marginTop="spacing.3"
          />
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" gap="spacing.5" justifyContent="flex-end">
          <Button variant="tertiary" onClick={onDismiss}>
            Cancel
          </Button>
          <Button
            isLoading={mutateSendForApproval.isLoading}
            onClick={() => mutateSendForApproval.mutate({ bankAccountId, reason: comment })}>
            Approve
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
